import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Radio,
  Camera,
  Play,
  Route,
  SlidersHorizontal,
  RotateCcw,
  FileText,
  ChevronDown,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { SCENARIO_REGISTRY } from '@/map/scenario/definitions';

const CAMERA_PRESETS = [
  { label: 'Overview', crumbs: ['North Vale', 'Overview'] },
  { label: 'Town Centre', crumbs: ['North Vale', 'Town Centre'] },
  { label: 'Depot', crumbs: ['North Vale', 'Depot'] },
  { label: 'Residential', crumbs: ['North Vale', 'Residential'] },
  { label: 'Control Tower', crumbs: ['North Vale', 'Control Tower'] },
];

const SCENARIOS = SCENARIO_REGISTRY.map((s) => ({ id: s.id, color: s.accent }));

const ROUTES = [
  { id: '60s-hook', label: '60s Hook', duration: '1 min' },
  { id: '8min-story', label: '8min Story', duration: '8 min' },
  { id: '20min-deep', label: '20min Deep Dive', duration: '20 min' },
];

export function OperatorControls() {
  const [visible, setVisible] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [speakerNotes, setSpeakerNotes] = useState('');

  const triggerScenario = useAppStore((s) => s.triggerScenario);
  const stopScenario = useAppStore((s) => s.stopScenario);
  const playRoute = useAppStore((s) => s.playRoute);
  const reset = useAppStore((s) => s.reset);
  const setTimeOfDay = useAppStore((s) => s.setTimeOfDay);
  const toggleXray = useAppStore((s) => s.toggleXray);
  const toggleAuthority = useAppStore((s) => s.toggleAuthority);
  const toggleVulnerable = useAppStore((s) => s.toggleVulnerable);
  const toggleShowAll = useAppStore((s) => s.toggleShowAll);
  const setBreadcrumb = useAppStore((s) => s.setBreadcrumb);
  const timeOfDay = useAppStore((s) => s.worldState.timeOfDay);
  const worldState = useAppStore((s) => s.worldState);
  const activeScenario = useAppStore((s) => s.scenarioState.activeScenario);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'O') {
        e.preventDefault();
        setVisible((v) => !v);
      }
      if (e.key === 'Escape' && visible) {
        setVisible(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: -320 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -320 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          className="pointer-events-auto absolute top-0 left-0 bottom-0 z-[90] w-80"
        >
          <div className="h-full bg-[var(--panel-bg)] backdrop-blur-2xl border-r border-[var(--border)] shadow-[4px_0_32px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
              <div className="flex items-center gap-3">
                <SlidersHorizontal size={16} className="text-[var(--text-secondary)]" />
                <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[var(--text-primary)]">
                  Operator
                </h2>
                <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/30">
                  <Radio size={10} className="text-red-400 animate-pulse" />
                  <span className="text-[10px] font-bold text-red-400 uppercase">Live</span>
                </span>
              </div>
              <button
                onClick={() => setVisible(false)}
                className="w-7 h-7 rounded-lg bg-[var(--badge-bg)] border border-[var(--border)] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--card-bg)] transition-all"
              >
                <X size={14} />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5 scrollbar-thin">
              {/* Camera Presets */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Camera size={13} className="text-[var(--text-muted)]" />
                  <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    Camera Presets
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {CAMERA_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => setBreadcrumb(preset.crumbs)}
                      className="px-3 py-2 bg-[var(--badge-bg)] border border-[var(--border)] hover:border-[var(--text-muted)] hover:bg-[var(--card-bg)] rounded-lg text-xs text-[var(--text-secondary)] font-medium transition-all duration-200"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* Scenarios Grid */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Play size={13} className="text-[var(--text-muted)]" />
                  <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    Scenarios
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {SCENARIOS.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => triggerScenario(s.id)}
                      className={`relative px-2 py-2 rounded-lg text-[11px] font-bold transition-all duration-200 border ${
                        activeScenario === s.id
                          ? 'bg-[var(--card-bg)] border-[var(--text-muted)] text-[var(--text-primary)]'
                          : 'bg-[var(--badge-bg)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {/* Top color indicator */}
                      <div
                        className="absolute top-0 left-2 right-2 h-0.5 rounded-b-full"
                        style={{
                          backgroundColor: s.color,
                          opacity: activeScenario === s.id ? 1 : 0.4,
                        }}
                      />
                      {s.id}
                    </button>
                  ))}
                </div>
                {activeScenario && (
                  <button
                    onClick={stopScenario}
                    className="mt-2 w-full px-3 py-1.5 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 rounded-lg text-xs text-red-400 font-medium transition-all"
                  >
                    Stop Current
                  </button>
                )}
              </section>

              {/* Routes */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Route size={13} className="text-[var(--text-muted)]" />
                  <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    Demo Routes
                  </h3>
                </div>
                <div className="space-y-1.5">
                  {ROUTES.map((route) => (
                    <button
                      key={route.id}
                      onClick={() => playRoute(route.id)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-[var(--badge-bg)] border border-[var(--border)] hover:border-[#00b8a9]/30 hover:bg-[#00b8a9]/5 rounded-lg transition-all duration-200 group"
                    >
                      <span className="text-xs font-medium text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]">
                        {route.label}
                      </span>
                      <span className="text-[10px] text-[var(--text-muted)] bg-[var(--badge-bg)] px-2 py-0.5 rounded">
                        {route.duration}
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              {/* Controls */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <SlidersHorizontal size={13} className="text-[var(--text-muted)]" />
                  <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    Controls
                  </h3>
                </div>

                {/* Time slider */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] text-[var(--text-muted)]">Time of Day</span>
                    <span className="text-[10px] text-[var(--text-secondary)] font-mono">
                      {Math.round(timeOfDay * 24).toString().padStart(2, '0')}:00
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={timeOfDay}
                    onChange={(e) => setTimeOfDay(parseFloat(e.target.value))}
                    className="w-full h-1 rounded-full appearance-none cursor-pointer bg-[var(--badge-bg)]
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:w-3
                      [&::-webkit-slider-thumb]:h-3
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:bg-[#00b8a9]
                      [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(0,184,169,0.5)]"
                  />
                </div>

                {/* Toggle buttons */}
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { label: 'X-Ray', active: worldState.xrayMode, toggle: toggleXray },
                    { label: 'Authority', active: worldState.authorityOverlay, toggle: toggleAuthority },
                    { label: 'Vulnerable', active: worldState.vulnerableFlags, toggle: toggleVulnerable },
                    { label: 'Show All', active: worldState.showAllActive, toggle: toggleShowAll },
                  ].map((item) => (
                    <button
                      key={item.label}
                      onClick={item.toggle}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border ${
                        item.active
                          ? 'bg-[#00b8a9]/15 border-[#00b8a9]/30 text-[#00b8a9]'
                          : 'bg-[var(--badge-bg)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* Reset */}
              <section>
                <button
                  onClick={reset}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 hover:border-red-500/40 rounded-xl text-sm text-red-400 font-semibold uppercase tracking-wider transition-all duration-200"
                >
                  <RotateCcw size={14} />
                  Reset Demo
                </button>
              </section>

              {/* Speaker Notes */}
              <section>
                <button
                  onClick={() => setNotesExpanded(!notesExpanded)}
                  className="flex items-center gap-2 mb-2 w-full"
                >
                  <FileText size={13} className="text-[var(--text-muted)]" />
                  <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    Speaker Notes
                  </h3>
                  <ChevronDown
                    size={12}
                    className={`text-[var(--text-muted)] ml-auto transition-transform duration-200 ${
                      notesExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {notesExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <textarea
                        value={speakerNotes}
                        onChange={(e) => setSpeakerNotes(e.target.value)}
                        className="w-full h-32 bg-[var(--card-bg)] border border-[var(--border)] rounded-lg p-3 text-xs text-[var(--text-secondary)] resize-none focus:outline-none focus:border-[#00b8a9]/30 placeholder:text-[var(--text-muted)] font-mono"
                        placeholder="Enter speaker notes for this demo session..."
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-[var(--border)] text-center">
              <span className="text-[10px] text-[var(--text-muted)] tracking-wide">
                Ctrl+Shift+O to toggle &bull; Esc to close
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
